"use client";

import { useGetCurrentUser } from "@/hooks/auth-hook";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { CountryCode, parsePhoneNumberFromString } from "libphonenumber-js";
import {
  ArrowRight,
  Check,
  ChevronDown,
  Eye,
  EyeOff,
  Lock,
  Mail,
  Phone,
  ShieldCheck,
  User,
} from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import Link from "next/link";
import { useState } from "react";
import { Controller } from "react-hook-form";
import { Triangle } from "react-loader-spinner";
import AlreadyLogin from "./AlreadyLogin";
import TernsAuth from "./TernsAuth";
import { useRegisterForm } from "./useRegisterForm";

export default function RegisterForm({ setErrorMessage }: any) {
  const t = useTranslations("Auth");
  const locale = useLocale();
  const isRtl = locale === "ar";
  const { data } = useGetCurrentUser();
  const logic = useRegisterForm(setErrorMessage, isRtl);

  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  if (data?.user) return <AlreadyLogin />;

  return (
    <div className="flex-1 flex flex-col justify-center px-6 md:px-16 lg:px-20 py-10 h-screen overflow-y-auto bg-white relative">
      <div className="w-full mx-auto">
        <div className="mb-10 text-center lg:text-start">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-50 border border-slate-100 mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-main animate-pulse" />
            <span className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-500">
              Exclusive Registry
            </span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-black text-slate-900 mb-4 tracking-tighter leading-tight">
            {t("signUp")}
          </h2>
        </div>

        <form
          onSubmit={logic.handleSubmit(logic.onSubmit)}
          className="space-y-5"
        >
          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            {["firstName", "lastName"].map((id) => (
              <div key={id} className="group space-y-1.5 text-start">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                  {t(id)}
                </label>
                <div className="relative">
                  <User
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-main transition-all"
                    size={18}
                  />
                  <input
                    {...logic.register(id, { required: true })}
                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 pl-12 pr-4 font-bold outline-none focus:border-main focus:bg-white transition-all text-slate-700"
                    placeholder={id === "firstName" ? "Yousef" : "Abdallah"}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Email Field */}
          <div className="group space-y-1.5 text-start">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
              {t("emailAddress")}
            </label>
            <div className="relative">
              <Mail
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-main transition-all"
                size={18}
              />
              <input
                {...logic.register("email", {
                  required: true,
                  pattern: /^\S+@\S+$/i,
                })}
                className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 pl-12 pr-4 font-bold outline-none focus:border-main focus:bg-white transition-all text-slate-700"
                placeholder="name@email.com"
              />
            </div>
          </div>

          {/* Phone Field */}
          <div className="group space-y-1.5 text-start">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
              {t("phoneNumber")}
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Country Picker - No Flags */}
              <div className="relative flex-1 sm:w-40 shrink-0">
                <select
                  {...logic.register("countryCode")}
                  className="w-full h-15.5 bg-slate-50 border border-slate-100 rounded-2xl pl-4 pr-10 font-black text-slate-700 outline-none cursor-pointer appearance-none hover:bg-slate-100 transition-colors text-xs"
                  defaultValue={`SA`}
                >
                  {logic.countriesList.map((c, idx) => {
                    return (
                      <option key={idx} value={c.iso}>
                        ({c.code}) {c.name}
                      </option>
                    );
                  })}
                </select>
                <ChevronDown
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  size={14}
                />
              </div>

              {/* Phone Input with Validation */}
              <div className="relative flex-2">
                <Phone
                  className={cn(
                    "absolute left-4 top-1/2 -translate-y-1/2 transition-all",
                    logic.errors.phone
                      ? "text-red-400"
                      : "text-slate-300 group-focus-within:text-main",
                  )}
                  size={18}
                />
                <Controller
                  name="phone"
                  control={logic.control}
                  rules={{
                    required: true,
                    validate: (val) =>
                      parsePhoneNumberFromString(
                        val,
                        logic.selectedCountry as CountryCode,
                      )?.isValid() || false,
                  }}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="tel"
                      className={cn(
                        "w-full h-15.5 bg-slate-50 border border-slate-100 rounded-2xl py-4 pl-12 pr-12 font-bold outline-none focus:border-main focus:bg-white transition-all text-slate-700",
                        logic.errors.phone && "border-red-400 bg-red-50/10",
                      )}
                      placeholder="5XXXXXXXX"
                    />
                  )}
                />
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  {logic.phoneValue?.length > 5 && !logic.errors.phone && (
                    <div className="bg-emerald-500 text-white p-1 rounded-full animate-in zoom-in">
                      <Check size={10} strokeWidth={4} />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <AnimatePresence>
              {logic.errors.phone && (
                <motion.p
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="text-[10px] font-bold text-red-500 ml-2 mt-1"
                >
                  {isRtl
                    ? "رقم الجوال غير متوافق مع الدولة المختارة"
                    : "Phone number invalid for selected country"}
                </motion.p>
              )}
            </AnimatePresence>
          </div>

          {/* Password Section */}
          <div className="space-y-4">
            {[
              {
                id: "password",
                label: t("password"),
                show: logic.showPassword,
                set: logic.setShowPassword,
              },
              {
                id: "confirmPassword",
                label: t("confirmPassword"),
                show: logic.showConfirmPassword,
                set: logic.setShowConfirmPassword,
              },
            ].map((f) => (
              <div key={f.id} className="group space-y-1.5 text-start">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                  {f.label}
                </label>
                <div className="relative">
                  <Lock
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-main transition-all"
                    size={18}
                  />
                  <input
                    type={f.show ? "text" : "password"}
                    {...logic.register(f.id, { required: true })}
                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 pl-12 pr-12 font-bold outline-none focus:border-main transition-all text-slate-700"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => f.set(!f.show)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-main transition-colors p-1"
                  >
                    {f.show ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            ))}

            {/* Password Safety Meter */}
            <div
              className={cn(
                "p-4 rounded-3xl border transition-all duration-500",
                logic.isPasswordValid
                  ? "bg-emerald-50 border-emerald-100"
                  : "bg-slate-50 border-slate-100",
              )}
            >
              <div className="flex justify-between items-center mb-3">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">
                  Safety Status
                </span>
                {logic.isPasswordValid && (
                  <ShieldCheck
                    size={14}
                    className="text-emerald-500 animate-pulse"
                  />
                )}
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                {logic.passwordRules.map((rule, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div
                      className={cn(
                        "w-3.5 h-3.5 rounded-full flex items-center justify-center transition-all",
                        rule.met
                          ? "bg-emerald-500 text-white"
                          : "bg-slate-200 text-transparent",
                      )}
                    >
                      <Check size={8} strokeWidth={5} />
                    </div>
                    <span
                      className={cn(
                        "text-[10px] font-bold transition-colors",
                        rule.met ? "text-slate-900" : "text-slate-400",
                      )}
                    >
                      {rule.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Terms and Conditions Checkbox */}
          <div className="flex items-center gap-3 pt-2 text-start">
            <input
              type="checkbox"
              id="terms"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="w-5 h-5 rounded-md border-slate-300 text-main focus:ring-main/50 transition-all cursor-pointer accent-main"
            />
            <label
              htmlFor="terms"
              className="text-sm font-bold text-slate-500 cursor-pointer select-none"
            >
              {isRtl ? "لقد قرأت وأوافق على " : "I have read and agree to the "}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setIsTermsOpen(true);
                }}
                className="text-slate-900 underline hover:text-main underline-offset-4 transition-colors cursor-pointer"
              >
                {isRtl ? "الشروط والأحكام" : "Terms and Conditions"}
              </button>
            </label>
          </div>

          <button
            disabled={
              logic.isPending ||
              !logic.isPasswordValid ||
              logic.passwordValue !== logic.confirmPasswordValue ||
              !termsAccepted
            }
            type="submit"
            className="w-full h-16 mt-4 bg-slate-900 text-white rounded-3xl font-black text-lg hover:bg-main hover:shadow-[0_20px_40px_rgba(var(--main-rgb),0.3)] transition-all disabled:opacity-20 flex items-center justify-center gap-3 group active:scale-[0.98] cursor-pointer disabled:cursor-not-allowed"
          >
            {logic.isPending ? (
              <Triangle height={24} width={24} color="#fff" />
            ) : (
              <>
                <span>{t("signUp")}</span>
                <ArrowRight
                  className={cn(
                    "w-5 h-5 transition-transform group-hover:translate-x-2",
                    isRtl && "rotate-180 group-hover:-translate-x-2",
                  )}
                />
              </>
            )}
          </button>
        </form>

        <p className="mt-8 text-center text-slate-400 font-bold text-sm tracking-tight">
          {t("haveAccount")}{" "}
          <Link
            href="/auth/login"
            className="text-slate-900 hover:text-main underline decoration-slate-200 underline-offset-8 transition-all hover:decoration-main"
          >
            {t("login")}
          </Link>
        </p>
      </div>

      {/* Terms & Conditions Modal */}
      <AnimatePresence>
        {isTermsOpen && (
          <TernsAuth
            isRtl={isRtl}
            setIsTermsOpen={setIsTermsOpen}
            setTermsAccepted={setTermsAccepted}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
